import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase-config';

export const COMMERCIAL_PLANS_DOC_PATH = ['appSettings', 'commercialPlans'];

export const BILLING_OPTIONS = [
  { key: 'monthly', label: 'Mensual', periodLabel: '/ mes' },
  { key: 'annual', label: 'Anual', periodLabel: '/ año' },
];

export const DEFAULT_COMMERCIAL_CATALOG = {
  currency: 'ARS',
  annualBadgeDefault: '20% de ahorro anual',
  plans: [
    {
      key: 'trial',
      title: 'Trial 30 días',
      eyebrow: 'Prueba activa',
      summary: 'Probá durante un mes la creación de rutas, unidades de transporte, choferes y destinos con acceso individual para marcar el trabajo diario.',
      featured: false,
      monthlyPrice: 0,
      annualPrice: 0,
      annualSavingsLabel: '',
      isFree: true,
      publicCta: 'Probar gratis 30 días',
      companyCta: 'Plan actual',
      features: [
        'Creación de rutas y hojas de trabajo',
        'Alta de unidades de transporte',
        'Alta de choferes y datos de contacto',
        'Acceso individual para cada chofer',
        'Marcación de destinos por chofer en tiempo real',
        'Base de clientes, contactos y direcciones frecuentes',
        'Sin tarjeta y sin pérdida de datos al finalizar',
      ],
    },
    {
      key: 'base',
      title: 'Base',
      eyebrow: 'Operación diaria',
      summary: 'Ordená la operación diaria creando unidades, choferes, rutas, destinos y accesos individuales para que cada chofer marque su recorrido.',
      featured: true,
      monthlyPrice: 49000,
      annualPrice: 470400,
      annualSavingsLabel: '20% de ahorro anual',
      publicCta: 'Quiero el plan Base',
      companyCta: 'Cambiar a Base',
      features: [
        'Todo lo del trial',
        'Entorno propio de la empresa',
        'Creación de unidades de transporte',
        'Creación de choferes y accesos individuales',
        'Cada chofer puede marcar destinos, entregas y retiros',
        'Base de clientes, proveedores, contactos y direcciones',
        'Vista individual por transporte o chofer',
        'No incluye control de stock avanzado',
      ],
    },
    {
      key: 'intermedio',
      title: 'Intermedio',
      eyebrow: 'Stock y monitoreo',
      summary: 'Sumá una vista general de todas las unidades en movimiento y un control de stock para compras, salidas, cantidades y movimientos.',
      featured: false,
      monthlyPrice: 79000,
      annualPrice: 758400,
      annualSavingsLabel: '20% de ahorro anual',
      publicCta: 'Quiero el plan Intermedio',
      companyCta: 'Cambiar a Intermedio',
      features: [
        'Todo lo del plan Base',
        'Vista general de varias unidades al mismo tiempo',
        'Mapa con rutas y choferes activos',
        'Control de productos, cantidades y categorías',
        'Registro de compras de mercadería',
        'Preparación de salidas de stock',
        'Planilla de movimientos: qué entró, qué salió y cuándo',
        'Control físico básico para comparar sistema contra depósito',
      ],
    },
    {
      key: 'avanzado',
      title: 'Avanzado',
      eyebrow: 'Trazabilidad profunda',
      summary: 'Completá el control con historia detallada de productos, garantías, devoluciones, reportes, gastos de flota y diferencia entre costo y venta.',
      featured: false,
      monthlyPrice: 119000,
      annualPrice: 1142400,
      annualSavingsLabel: '20% de ahorro anual',
      publicCta: 'Quiero el plan Avanzado',
      companyCta: 'Cambiar a Avanzado',
      features: [
        'Todo lo del plan Intermedio',
        'Historia por producto: cuándo entró, cuánto costó y cuándo salió',
        'Registro de destino, transporte, chofer y receptor',
        'Garantías, devoluciones e incidentes',
        'Días guardado en depósito o fuera de stock físico',
        'Diferencia entre costo de compra y precio de salida',
        'Reportes, balance general y gastos de flota',
        'Preparado para QR, código de barras, fotos y evidencia',
      ],
    },
  ],
};

const parseNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(normalized) ? normalized : fallback;
  }
  return fallback;
};

export const normalizeCommercialPlanKey = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!normalized) return 'base';
  if (['trial', 'plan trial'].includes(normalized)) return 'trial';
  if (['base', 'basico', 'básico', 'basic'].includes(normalized)) return 'base';
  if (['intermedio', 'intermediate'].includes(normalized)) return 'intermedio';
  if (['avanzado', 'advanced', 'premium'].includes(normalized)) return 'avanzado';
  return normalized;
};

const normalizeBillingCycle = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return normalized === 'annual' || normalized === 'anual' ? 'annual' : 'monthly';
};

export const formatCommercialPrice = (amount, currency = 'ARS') => {
  const safeAmount = parseNumber(amount, 0);
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(safeAmount);
};

export const resolveCommercialCatalog = (input = {}) => {
  const sourcePlans = Array.isArray(input?.plans) ? input.plans : [];
  const mergedPlans = DEFAULT_COMMERCIAL_CATALOG.plans.map((defaultPlan) => {
    const override = sourcePlans.find((plan) => normalizeCommercialPlanKey(plan?.key) === defaultPlan.key) || {};
    return {
      ...defaultPlan,
      ...override,
      key: defaultPlan.key,
      title: override?.title || defaultPlan.title,
      eyebrow: override?.eyebrow || defaultPlan.eyebrow,
      summary: override?.summary || defaultPlan.summary,
      featured: typeof override?.featured === 'boolean' ? override.featured : defaultPlan.featured,
      monthlyPrice: defaultPlan.key === 'trial' ? 0 : parseNumber(override?.monthlyPrice, defaultPlan.monthlyPrice),
      annualPrice: defaultPlan.key === 'trial' ? 0 : parseNumber(override?.annualPrice, defaultPlan.annualPrice),
      annualSavingsLabel:
        typeof override?.annualSavingsLabel === 'string' && override.annualSavingsLabel.trim()
          ? override.annualSavingsLabel.trim()
          : defaultPlan.annualSavingsLabel || input?.annualBadgeDefault || DEFAULT_COMMERCIAL_CATALOG.annualBadgeDefault,
      publicCta: override?.publicCta || defaultPlan.publicCta,
      companyCta: override?.companyCta || defaultPlan.companyCta,
      isFree: defaultPlan.key === 'trial',
      features:
        Array.isArray(override?.features) && override.features.length
          ? override.features.map((item) => String(item || '').trim()).filter(Boolean)
          : defaultPlan.features,
    };
  });

  return {
    currency: input?.currency || DEFAULT_COMMERCIAL_CATALOG.currency,
    annualBadgeDefault: input?.annualBadgeDefault || DEFAULT_COMMERCIAL_CATALOG.annualBadgeDefault,
    plans: mergedPlans,
  };
};

export const resolveRuntimeCommercialCatalog = (input = {}) => {
  const sourcePlans = Array.isArray(input?.plans) ? input.plans : [];
  const mergedPlans = DEFAULT_COMMERCIAL_CATALOG.plans.map((defaultPlan) => {
    const override = sourcePlans.find((plan) => normalizeCommercialPlanKey(plan?.key) === defaultPlan.key) || {};
    return {
      ...defaultPlan,
      key: defaultPlan.key,
      title: defaultPlan.title,
      eyebrow: defaultPlan.eyebrow,
      summary: defaultPlan.summary,
      featured: typeof override?.featured === 'boolean' ? override.featured : defaultPlan.featured,
      monthlyPrice: defaultPlan.key === 'trial' ? 0 : parseNumber(override?.monthlyPrice, defaultPlan.monthlyPrice),
      annualPrice: defaultPlan.key === 'trial' ? 0 : parseNumber(override?.annualPrice, defaultPlan.annualPrice),
      annualSavingsLabel:
        typeof override?.annualSavingsLabel === 'string' && override.annualSavingsLabel.trim()
          ? override.annualSavingsLabel.trim()
          : defaultPlan.annualSavingsLabel || input?.annualBadgeDefault || DEFAULT_COMMERCIAL_CATALOG.annualBadgeDefault,
      publicCta: override?.publicCta || defaultPlan.publicCta,
      companyCta: override?.companyCta || defaultPlan.companyCta,
      isFree: defaultPlan.key === 'trial',
      features: defaultPlan.features,
    };
  });

  return {
    currency: input?.currency || DEFAULT_COMMERCIAL_CATALOG.currency,
    annualBadgeDefault: input?.annualBadgeDefault || DEFAULT_COMMERCIAL_CATALOG.annualBadgeDefault,
    plans: mergedPlans,
  };
};

export const useCommercialCatalog = () => {
  const [catalog, setCatalog] = useState(DEFAULT_COMMERCIAL_CATALOG);

  useEffect(() => {
    const ref = doc(db, ...COMMERCIAL_PLANS_DOC_PATH);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setCatalog(DEFAULT_COMMERCIAL_CATALOG);
          return;
        }
        setCatalog(resolveRuntimeCommercialCatalog(snapshot.data() || {}));
      },
      () => setCatalog(DEFAULT_COMMERCIAL_CATALOG)
    );

    return () => unsubscribe();
  }, []);

  return { catalog };
};

const BillingCycleSwitch = ({ billingCycle, onChange, align = 'left' }) => {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${align === 'right' ? 'justify-start lg:justify-end' : ''}`}>
      <div className="inline-flex flex-wrap rounded-2xl border border-slate-200 bg-slate-100 p-1 shadow-sm">
        {BILLING_OPTIONS.map((option) => {
          const active = billingCycle === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange?.(option.key)}
              className={`rounded-xl whitespace-nowrap px-4 py-2 text-sm font-semibold transition ${
                active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PlanCard = ({ plan, billingCycle, currency, isCurrent, mode, onAction, compact = false }) => {
  const billingOption = BILLING_OPTIONS.find((o) => o.key === billingCycle) || BILLING_OPTIONS[0];
  const isTrial = plan.key === 'trial';
  const displayPrice = isTrial
    ? 'Gratis'
    : formatCommercialPrice(billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice, currency);

  const actionLabel = isCurrent
    ? 'Plan actual'
    : mode === 'company'
      ? plan.companyCta || `Cambiar a ${plan.title}`
      : plan.publicCta || `Solicitar ${plan.title}`;
  const visibleFeatures = compact ? plan.features.slice(0, 4) : plan.features;

  return (
    <article
      className={`flex h-full flex-col border bg-white shadow-[0_18px_48px_rgba(15,23,42,0.08)] transition-transform duration-200 hover:-translate-y-1 ${
        compact ? 'rounded-[24px] p-4' : 'rounded-[30px] p-6'
      } ${
        plan.featured ? 'border-cyan-300 ring-1 ring-cyan-200' : 'border-slate-200'
      } ${isCurrent ? 'ring-2 ring-cyan-500/60' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-700">{plan.eyebrow}</p>
          <h3 className={`${compact ? 'mt-2 text-xl' : 'mt-3 text-2xl'} font-black tracking-tight text-slate-900`}>{plan.title}</h3>
        </div>
        <div className="flex flex-col items-end gap-2">
          {plan.featured && (
            <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-cyan-800">
              Recomendado
            </span>
          )}
          {isCurrent && (
            <span className="rounded-full border border-slate-900 bg-slate-900 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white">
              Plan actual
            </span>
          )}
        </div>
      </div>

      <div className={`${compact ? 'mt-3 rounded-2xl px-4 py-3' : 'mt-5 rounded-3xl px-5 py-4'} border border-slate-200 bg-slate-50`}>
        <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
          <span className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-black tracking-tight text-slate-950`}>{displayPrice}</span>
          {!isTrial && (
            <span className="pb-1 text-xs font-semibold whitespace-nowrap text-slate-500 md:text-sm">
              {billingOption.periodLabel}
            </span>
          )}
        </div>
        {!isTrial && billingCycle === 'annual' && plan.annualSavingsLabel && (
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">{plan.annualSavingsLabel}</p>
        )}
      </div>

      <p className={`${compact ? 'mt-3 max-h-10 overflow-hidden text-xs leading-5' : 'mt-5 text-sm leading-6'} text-slate-600`}>{plan.summary}</p>

      <div className={`${compact ? 'mt-3' : 'mt-5'} flex-1`}>
        <p className={`${compact ? 'text-xs' : 'text-sm'} font-bold text-slate-900`}>Incluye</p>
        <ul className={`${compact ? 'mt-2 space-y-1 text-xs leading-5' : 'mt-3 space-y-2 text-sm'} text-slate-700`}>
          {visibleFeatures.map((item) => (
            <li key={`${plan.key}-${item}`} className="flex items-start gap-2">
              <span className={`${compact ? 'mt-2' : 'mt-[9px]'} h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-600`} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        onClick={() => onAction?.(plan)}
        disabled={isCurrent}
        className={`${compact ? 'mt-4 rounded-xl px-3 py-2.5 text-xs' : 'mt-6 rounded-2xl px-4 py-3 text-sm'} font-semibold transition ${
          isCurrent
            ? 'cursor-default border border-slate-900 bg-slate-900 text-white'
            : plan.featured
              ? 'bg-cyan-600 text-white hover:bg-cyan-700'
              : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
        }`}
      >
        {actionLabel}
      </button>
    </article>
  );
};

export const PlansCatalogSection = ({
  catalog,
  billingCycle = 'monthly',
  onBillingCycleChange,
  currentPlanKey,
  companyName = '',
  isTrial = false,
  mode = 'public',
  onPlanAction,
  className = '',
}) => {
  const safeCatalog = useMemo(() => resolveCommercialCatalog(catalog), [catalog]);
  const normalizedCurrentPlanKey = mode === 'company'
    ? normalizeCommercialPlanKey(currentPlanKey || (isTrial ? 'trial' : 'base'))
    : null;
  const normalizedBillingCycle = normalizeBillingCycle(billingCycle);
  const compactPublic = mode === 'public';

  return (
    <section className={className}>
      {mode === 'company' ? (
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-slate-900 bg-slate-900 px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-white">
                Plan actual
              </span>
              {companyName && (
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {companyName}
                </span>
              )}
            </div>
            {isTrial && (
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600 md:text-[15px]">
                Tu empresa ya está operando con el entorno base de G-CENTRA. Tenés acceso al núcleo operativo para probar rutas, fichas, base de contactos, accesos individuales por transporte y seguimiento real desde la hoja de trabajo.
              </p>
            )}
          </div>
          <BillingCycleSwitch billingCycle={normalizedBillingCycle} onChange={onBillingCycleChange} align="right" />
        </div>
      ) : (
        <div className={`${compactPublic ? 'mb-4' : 'mb-6'} flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between`}>
          <div className="max-w-3xl">
            <p className="text-sm leading-6 text-slate-600">
              Compará mensual o anual y elegí el escalamiento que mejor acompañe el momento de tu empresa.
            </p>
          </div>
          <BillingCycleSwitch billingCycle={normalizedBillingCycle} onChange={onBillingCycleChange} align="right" />
        </div>
      )}

      <div className={`grid items-stretch ${compactPublic ? 'gap-3' : 'gap-5'} md:grid-cols-2 xl:grid-cols-4`}>
        {safeCatalog.plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            billingCycle={normalizedBillingCycle}
            currency={safeCatalog.currency}
            isCurrent={Boolean(normalizedCurrentPlanKey) && plan.key === normalizedCurrentPlanKey}
            mode={mode}
            onAction={onPlanAction}
            compact={compactPublic}
          />
        ))}
      </div>
    </section>
  );
};

const CompanyPlanPanel = ({
  companyName = '',
  companyPlanMeta,
  isTrial = false,
  onPlanAction,
  billingCycle = 'monthly',
  onBillingCycleChange,
}) => {
  const { catalog } = useCommercialCatalog();

  const currentPlanKey = normalizeCommercialPlanKey(
    companyPlanMeta?.key || companyPlanMeta?.label || (isTrial ? 'trial' : 'base')
  );

  const handlePlanAction = (plan) => {
    onPlanAction?.(plan);
  };

  return (
    <section className="border-b border-slate-200 bg-white px-4 pb-6 pt-2 md:px-6">
      <div className="mx-auto w-full max-w-7xl rounded-[32px] border border-slate-200 bg-white p-5 shadow-[0_18px_48px_rgba(15,23,42,0.08)] md:p-6">
        <PlansCatalogSection
          catalog={catalog}
          billingCycle={billingCycle}
          onBillingCycleChange={onBillingCycleChange}
          currentPlanKey={currentPlanKey}
          companyName={companyName}
          isTrial={isTrial}
          mode="company"
          onPlanAction={handlePlanAction}
        />
      </div>
    </section>
  );
};

export default CompanyPlanPanel;
